import { Injectable } from '@angular/core';
import { CdkVirtualScrollViewport, VirtualScrollStrategy } from '@angular/cdk/scrolling';
import { ListRange } from '@angular/cdk/collections';

import { distinctUntilChanged } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface TSVStrategyConfigs {
  rowHeight: number;
  headerHeight: number;
  footerHeight: number;
  bufferMultiplier: number;
}

@Injectable()
export class FixedSizeTableVirtualScrollStrategy implements VirtualScrollStrategy {
  public viewport: CdkVirtualScrollViewport;
  public renderedRangeStream = new BehaviorSubject<ListRange>({ start: 0, end: 0 });
  public scrolledIndexChange: Observable<number>;
  public stickyChange = new Subject<number>();

  private rowHeight!: number;
  private headerHeight!: number;
  private footerHeight!: number;
  private bufferMultiplier!: number;
  private indexChange = new Subject<number>();
  private _dataLength = 0;

  public get dataLength(): number {
    return this._dataLength;
  }

  public set dataLength(value: number) {
    if (value !== this._dataLength) {
      this._dataLength = value;
      this.onDataLengthChanged();
    }
  }

  constructor() {
    this.scrolledIndexChange = this.indexChange.pipe(distinctUntilChanged());
  }

  public attach(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport;
    this.viewport.renderedRangeStream.subscribe(this.renderedRangeStream);
    this.stickyChange.next(0);
    this.onDataLengthChanged();
  }

  public detach(): void {
    this.indexChange.complete();
    this.stickyChange.complete();
    this.renderedRangeStream.complete();
  }

  public onContentScrolled(): void {
    this.updateContent();
  }

  public onDataLengthChanged(): void {
    if (this.viewport) {
      const contentSize = this.dataLength * this.rowHeight + this.headerHeight + this.footerHeight;
      this.viewport.setTotalContentSize(contentSize);
      const viewportSize = this.viewport.getViewportSize();
      if (this.viewport.measureScrollOffset() + viewportSize >= contentSize) {
        this.viewport.scrollToOffset(contentSize - viewportSize);
      }
    }
    this.updateContent();
  }

  public onContentRendered(): void {}

  public onRenderedOffsetChanged(): void {}

  public scrollToIndex(index: number, behavior?: ScrollBehavior): void {
    if (!this.viewport || !this.rowHeight) {
      return;
    }
    this.viewport.scrollToOffset((index - 1) * this.rowHeight + this.headerHeight, behavior);
  }

  public setConfig(configs: TSVStrategyConfigs) {
    const { rowHeight, headerHeight, footerHeight, bufferMultiplier } = configs;
    if (
      this.rowHeight === rowHeight &&
      this.headerHeight === headerHeight &&
      this.footerHeight === footerHeight &&
      this.bufferMultiplier === bufferMultiplier
    ) {
      return;
    }
    this.rowHeight = rowHeight;
    this.headerHeight = headerHeight;
    this.footerHeight = footerHeight;
    this.bufferMultiplier = bufferMultiplier;
    this.onDataLengthChanged();
  }

  private updateContent() {
    if (!this.viewport || !this.rowHeight) {
      return;
    }

    // Calculate the offset to the start of the rendered content
    const renderedOffset = this.viewport.getOffsetToRenderedContentStart();
    // Determine the start index based on the rendered offset and row height
    const start = renderedOffset / this.rowHeight;
    // Calculate the number of items that can be displayed in the viewport
    const itemsDisplayed = Math.ceil(this.viewport.getViewportSize() / this.rowHeight);
    // Calculate the number of buffer items to render before and after the visible items
    const bufferItems = Math.ceil(itemsDisplayed * this.bufferMultiplier);
    // Determine the end index based on the start index, items displayed, and buffer items
    const end = start + itemsDisplayed + 2 * bufferItems;

    // Calculate the buffer offset and the current scroll offset
    const bufferOffset = renderedOffset + bufferItems * this.rowHeight;
    const scrollOffset = this.viewport.measureScrollOffset();

    // Calculate how far the scroll offset is from the lower buffer, which is usually where items start being displayed
    const relativeScrollOffset = scrollOffset - bufferOffset;
    // Determine the number of rows scrolled based on the relative scroll offset and row height
    const rowsScrolled = relativeScrollOffset / this.rowHeight;

    // Calculate the displayed index based on the scroll offset and row height
    const displayed = scrollOffset / this.rowHeight;
    this.indexChange.next(displayed);

    // Only bother updating the displayed information if we've scrolled more than a row
    const rowSensitivity = 1.0;
    if (Math.abs(rowsScrolled) < rowSensitivity) {
      this.viewport.setRenderedContentOffset(renderedOffset);
      this.viewport.setRenderedRange({ start, end });
      return;
    }

    // Special case for the start of the table.
    // At the top of the table, the first few rows are first rendered because they're visible, and then still rendered
    // Because they move into the buffer. So we only need to change what's rendered once the user scrolls far enough down.
    if (renderedOffset === 0 && rowsScrolled < 0) {
      this.viewport.setRenderedContentOffset(renderedOffset);
      this.viewport.setRenderedRange({ start, end });
      return;
    }

    // Calculate the number of rows to move based on the rows scrolled
    const rowsToMove = Math.sign(rowsScrolled) * Math.floor(Math.abs(rowsScrolled));
    // Adjust the rendered offset based on the rows to move
    const adjustedRenderedOffset = Math.max(0, renderedOffset + rowsToMove * this.rowHeight);
    this.viewport.setRenderedContentOffset(adjustedRenderedOffset);

    // Adjust the start and end indices based on the rows to move
    const adjustedStart = Math.max(0, start + rowsToMove);
    const adjustedEnd = adjustedStart + itemsDisplayed + 2 * bufferItems;
    this.viewport.setRenderedRange({ start: adjustedStart, end: adjustedEnd });

    // Notify about the sticky change
    this.stickyChange.next(adjustedRenderedOffset);
  }
}
